import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { form, pattern, required, submit } from '@angular/forms/signals';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { CustomDomain } from '@tourfold/libraries/tf-api-client-frontend-v2';
import {
  ServerError,
  SignalFormServerErrors,
  extractServerErrors,
} from '@tourfold/libraries/shared-util';
import { CustomDomainsService } from '../custom-domains/custom-domains.service';
import { SettingsInlineMessage } from '@tourfold/features/settings-ui';

type WizardStep = 'enter' | 'verify' | 'verify-fail' | 'verify-success' | 'registry-blocked';

type CopyField = 'txt-name' | 'txt-value' | 'cname-name' | 'cname-target';

interface DomainFormData {
  domain: string;
}

const EMPTY_FORM: DomainFormData = { domain: '' };

const DOMAIN_FORM_FIELDS = ['domain'] as const satisfies readonly (keyof DomainFormData)[];

// Permissive client-side check; the backend owns the canonical validation.
const DOMAIN_PATTERN = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;

// Apex domains (`example.com`) cannot host a CNAME at the zone root. The backend
// enforces this with the same heuristic — keep them in lockstep so the inline
// preflight matches the server's rejection.
const APEX_MIN_LABELS = 3;

// Sub-problem `type` URIs emitted by the verify endpoint when DNS records are wrong.
// Must stay in lockstep with `CustomDomainProblem.TXT_RECORD_MISSING` / `CNAME_RECORD_MISMATCH`
// on the backend.
const TXT_PROBLEM_SUFFIX = 'custom-domain-txt-record-missing';
const CNAME_PROBLEM_SUFFIX = 'custom-domain-cname-record-mismatch';

@Component({
  selector: 'tf-settings-add-custom-domain-dialog',
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    MessageModule,
    TooltipModule,
    SettingsInlineMessage,
  ],
  templateUrl: './add-custom-domain-dialog.html',
  styleUrl: './add-custom-domain-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddCustomDomainDialog {
  private readonly service = inject(CustomDomainsService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  /** When set, the wizard opens straight to the verify step for this domain. */
  readonly initialDomain = input<CustomDomain | null>(null);
  /** Whether the dialog starts on the enter form or the verify step. */
  readonly initialStep = input<'enter' | 'verify'>('enter');

  readonly closed = output<void>();
  readonly created = output<CustomDomain>();
  readonly updated = output<CustomDomain>();

  readonly visible = signal(true);
  readonly step = signal<WizardStep>('enter');
  readonly currentDomain = signal<CustomDomain | null>(null);
  readonly isSubmitting = signal(false);
  readonly copiedField = signal<CopyField | null>(null);
  readonly apexError = signal<string | null>(null);
  readonly generalError = signal<string | null>(null);
  readonly txtFailure = signal<ServerError | null>(null);
  readonly cnameFailure = signal<ServerError | null>(null);

  readonly formModel = signal<DomainFormData>({ ...EMPTY_FORM });
  readonly domainForm = form(this.formModel, (schema) => {
    required(schema.domain, { message: 'SETTINGS_CUSTOM_DOMAINS.WIZARD.DOMAIN_LABEL' });
    pattern(schema.domain, DOMAIN_PATTERN, {
      message: 'SETTINGS_CUSTOM_DOMAINS.WIZARD.DOMAIN_LABEL',
    });
  });
  readonly serverErrors = new SignalFormServerErrors(this.formModel, DOMAIN_FORM_FIELDS);

  readonly dialogTitle = computed(() => {
    switch (this.step()) {
      case 'verify-success':
        return 'SETTINGS_CUSTOM_DOMAINS.WIZARD.SUCCESS_DIALOG_TITLE';
      case 'registry-blocked':
        return 'SETTINGS_CUSTOM_DOMAINS.WIZARD.REGISTRY_BLOCKED_TITLE';
      default:
        return 'SETTINGS_CUSTOM_DOMAINS.WIZARD.TITLE';
    }
  });

  /**
   * Stepper index — 1 = "Enter domain" active, 2 = "Verify ownership" active,
   * 3 = both steps complete (verify-success). The stepper is rendered in
   * `enter` / `verify` / `verify-fail` / `verify-success`; hidden on terminal
   * states where the step concept does not apply (e.g. registry-blocked).
   */
  readonly stepperIndex = computed<number>(() => {
    switch (this.step()) {
      case 'enter':
        return 1;
      case 'verify':
      case 'verify-fail':
        return 2;
      case 'verify-success':
        return 3;
      default:
        return 0;
    }
  });

  readonly showStepper = computed(() => this.stepperIndex() > 0);

  constructor() {
    // Initialise from inputs when the wizard mounts. The host shows / hides this
    // dialog by re-creating it (`@if (wizardVisible())`), so the constructor runs
    // every time the wizard opens.
    effect(() => {
      const initial = this.initialDomain();
      if (initial) {
        this.currentDomain.set(initial);
        this.formModel.set({ domain: initial.domain });
        this.step.set(this.initialStep());
      } else {
        this.step.set('enter');
      }
    });
  }

  onAddDomain(): void {
    if (this.isSubmitting()) return;
    const trimmed = this.formModel().domain.trim().toLowerCase();
    this.generalError.set(null);
    if (trimmed && trimmed.split('.').length < APEX_MIN_LABELS) {
      this.apexError.set(
        this.translate.instant('SETTINGS_CUSTOM_DOMAINS.WIZARD.APEX_ERROR', {
          example: `tourfold.${trimmed}`,
        })
      );
      return;
    }
    this.apexError.set(null);
    submit(this.domainForm, async () => {
      this.isSubmitting.set(true);
      this.serverErrors.clear();
      try {
        const created = await firstValueFrom(this.service.create({ domain: trimmed }));
        this.currentDomain.set(created);
        this.created.emit(created);
        this.step.set('verify');
      } catch (err) {
        const applied = await this.serverErrors.apply(err);
        if (applied && applied.generalMessages.length > 0) {
          this.generalError.set(applied.generalMessages.join(' '));
        } else if (!applied) {
          this.generalError.set(this.translate.instant('SETTINGS_CUSTOM_DOMAINS.ERROR.CREATE_FAILED'));
        }
      } finally {
        this.isSubmitting.set(false);
      }
    });
  }

  onDomainInputChange(value: string): void {
    this.formModel.set({ domain: value });
    if (this.apexError() !== null) {
      this.apexError.set(null);
    }
  }

  async onVerifyNow(): Promise<void> {
    const domain = this.currentDomain();
    if (!domain || this.isSubmitting()) return;
    this.isSubmitting.set(true);
    this.generalError.set(null);
    this.txtFailure.set(null);
    this.cnameFailure.set(null);
    try {
      const result = await firstValueFrom(this.service.verify(domain.id));
      this.currentDomain.set(result);
      this.updated.emit(result);
      this.step.set('verify-success');
    } catch (err) {
      const subProblems = await extractServerErrors(err);
      const txt = subProblems.find((sp) => sp.type?.endsWith(TXT_PROBLEM_SUFFIX));
      const cname = subProblems.find((sp) => sp.type?.endsWith(CNAME_PROBLEM_SUFFIX));
      if (txt || cname) {
        this.txtFailure.set(txt ?? null);
        this.cnameFailure.set(cname ?? null);
        this.step.set('verify-fail');
      } else {
        this.generalError.set(this.translate.instant('SETTINGS_CUSTOM_DOMAINS.ERROR.VERIFY_FAILED'));
      }
    } finally {
      this.isSubmitting.set(false);
    }
  }

  onBackToVerify(): void {
    this.generalError.set(null);
    this.txtFailure.set(null);
    this.cnameFailure.set(null);
    this.step.set('verify');
  }

  clearGeneralError(): void {
    this.generalError.set(null);
  }

  /**
   * Pulls `actual_targets` from a CNAME sub-problem and renders it as a comma-separated string,
   * or returns null when the resolver returned nothing (so the template can fall back to a
   * "no CNAME found" message instead of saying "points to (nothing)").
   */
  cnameActualTargets(failure: ServerError): string | null {
    const actuals = failure.data?.['actual_targets'];
    if (Array.isArray(actuals) && actuals.length > 0) {
      return actuals.join(', ');
    }
    return null;
  }

  /** Success step's "Open in Brands" link — navigates to brands settings + closes the dialog. */
  openBrandsSettings(): void {
    this.closeDialog();
    void this.router.navigate(['/settings/brands']);
  }

  async copyToClipboard(value: string, field: CopyField): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      this.copiedField.set(field);
      setTimeout(() => {
        if (this.copiedField() === field) {
          this.copiedField.set(null);
        }
      }, 1500);
    } catch {
      this.copiedField.set(null);
    }
  }

  onCancel(): void {
    if (this.isSubmitting()) return;
    this.closeDialog();
  }

  onDialogVisibleChange(visible: boolean): void {
    if (!visible) {
      this.closeDialog();
    }
  }

  private closeDialog(): void {
    this.visible.set(false);
    this.closed.emit();
  }
}
